/**
 * Bean Snake — board-first arcade surface.
 *
 * Nik's call: "leaderboard front and centre" — the board is the screen, and
 * playing is a button on it, not the reverse. `view` starts at 'board' and
 * only ever leaves it because the player tapped a play button; it always
 * comes back to 'board' when a run ends.
 *
 * Two lanes, and the difference is load-bearing:
 *  - PRACTICE is free, unlimited, and entirely client-side: it mints its own
 *    seed with Math.random and never calls startRun. That's precisely what
 *    makes unlimited free practice impossible to abuse — the free lane has
 *    no backend to abuse. It must never import or call `startRun`.
 *  - RANKED spends one of the week's tries via `startRun()`, which hands back
 *    a server-issued seed, and its result is submitted back for the board.
 *
 * `runToken` (not `seed` alone) is the SnakeCanvas `key`. SnakeCanvas reads
 * its `seed` prop exactly once, at mount (see SnakeCanvas.tsx) — changing the
 * prop on a live instance starts no new game. A `key` change is what forces
 * React to unmount/remount. Keying on `runToken` (a strictly-incrementing
 * counter) rather than `seed` itself also survives the astronomically
 * unlikely case of two consecutive practice seeds colliding.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { ENGINE_VERSION, type InputEvent } from '@shared/arcade/types';
import Leaderboard from './Leaderboard';
import SnakeCanvas, { type SnakeCanvasHandle } from './SnakeCanvas';
import { useAuthStore } from '../../store/authStore';
import {
  fetchBoard, startRun, submitRun, flushQueuedRuns, reportPlayer,
} from '../../services/arcade';
import { ApiError } from '../../services/api-client';

type View = 'board' | 'playing-ranked' | 'playing-practice';
type Scope = 'global' | 'shop';
type Lane = 'ranked' | 'practice' | null;
type BoardResult = Awaited<ReturnType<typeof fetchBoard>>;

interface ArcadePanelProps {
  shopId: string | null;
  onClose: () => void;
}

// Copy is dictated by the server's `reason` on a no_tries rejection — these
// are the only two reasons the endpoint sends, so an unrecognised reason (or
// none at all) falls back to a generic retry line rather than showing nothing.
const TRIES_MESSAGES: Record<string, string> = {
  get_stamped: "Get stamped to enter this week's board",
  spent: "You've used this week's runs — get stamped for 3 more",
};

// Same per-address namespacing rationale as arcade.ts's queueKey: a flat key
// would let one account's cached best leak onto another account signed into
// the same device.
function personalBestKey(address: string): string {
  return `bitbrew.arcade.personalBest.${address}`;
}

function readPersonalBest(address: string | null): number {
  if (!address) return 0;
  try {
    const raw = localStorage.getItem(personalBestKey(address));
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function writePersonalBest(address: string, score: number): void {
  try {
    if (score > readPersonalBest(address)) {
      localStorage.setItem(personalBestKey(address), String(score));
    }
  } catch { /* quota — best effort, not worth failing the run over */ }
}

const CHIP_ACTIVE = 'pixel-corners-sm bg-white text-primary font-grotesk font-bold text-[12px] px-3.5 py-2';
const CHIP_INACTIVE = 'pixel-corners-sm bg-black/[.18] text-white font-grotesk font-bold text-[12px] px-3.5 py-2';

export default function ArcadePanel({ shopId, onClose }: ArcadePanelProps) {
  const { userAddress } = useAuthStore();

  const [view, setView] = useState<View>('board');
  const [scope, setScope] = useState<Scope>('global');
  const [board, setBoard] = useState<BoardResult | null>(null);
  const [boardLoading, setBoardLoading] = useState(true);
  const [boardError, setBoardError] = useState<string | null>(null);

  const [runId, setRunId] = useState<string | null>(null);
  const [seed, setSeed] = useState(0);
  const [runToken, setRunToken] = useState(0);
  const [startBusy, setStartBusy] = useState(false);
  const [triesMessage, setTriesMessage] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [personalBest, setPersonalBest] = useState(() => readPersonalBest(userAddress));
  const [reportStatus, setReportStatus] = useState<'idle' | 'sent' | 'error'>('idle');

  // Which lane is currently live, tracked separately from `view`. `view`
  // drives what's on screen; `activeLane` drives what a game-over or an
  // in-run close should DO. Deciding "was this a ranked run" from `view`
  // alone is one render behind the moment a close handler already flipped
  // it back to 'board' — branching on this instead keeps that decision
  // correct even if a stray late callback fires after the view has moved on.
  const [activeLane, setActiveLane] = useState<Lane>(null);
  // A ranked run's result must be submitted exactly once, whichever of the
  // two paths gets there first — natural game-over (SnakeCanvas's own
  // onGameOver) or the player closing out mid-run (handleClose, below).
  // Reset to false at the start of every new ranked run.
  const rankedSubmittedRef = useRef(false);
  // Imperative escape hatch into the live SnakeCanvas instance — see
  // SnakeCanvas.tsx's SnakeCanvasHandle. Only ever read from handleClose.
  const canvasHandleRef = useRef<SnakeCanvasHandle>(null);

  const loadBoard = useCallback((nextScope: Scope, nextShopId: string | undefined) => {
    setBoardLoading(true);
    setBoardError(null);
    fetchBoard(nextScope, nextShopId)
      .then((result) => setBoard(result))
      .catch(() => setBoardError("Couldn't load the board — check your connection."))
      .finally(() => setBoardLoading(false));
  }, []);

  // Mount only: land on the global board, and flush any run stranded offline
  // from a previous session. `userAddress` is read from the closure (the
  // auth store resolves synchronously before first render, per authStore.ts)
  // rather than listed as a dep — this must run once, not on every re-render.
  useEffect(() => {
    loadBoard('global', undefined);
    if (userAddress) {
      flushQueuedRuns(userAddress)
        .then((result) => {
          // Losing a run is acceptable (the server genuinely will not score
          // one flushed past its 30-minute expiry); losing it SILENTLY is
          // not — see I3 in the whole-branch review.
          if (result.expired > 0) {
            setStatusMessage(
              result.expired === 1
                ? 'A run from earlier expired before it could be posted.'
                : `${result.expired} runs from earlier expired before they could be posted.`,
            );
          }
        })
        .catch(() => { /* retried on next open */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPersonalBest(readPersonalBest(userAddress));
  }, [userAddress]);

  function handleScopeChange(nextScope: Scope) {
    if (nextScope === 'shop' && !shopId) return; // disabled — no shop to scope to
    setScope(nextScope);
    loadBoard(nextScope, nextScope === 'shop' ? (shopId ?? undefined) : undefined);
  }

  async function handlePlayRanked() {
    // Guards a double-tap from spending two tries on one press — the button
    // is also `disabled` while busy, this is the belt-and-braces backstop.
    if (startBusy) return;
    setStartBusy(true);
    setTriesMessage(null);
    setStatusMessage(null);
    try {
      const result = await startRun();
      // A stale service-worker bundle can be running old client rules
      // against a server whose engine has since moved on — the server would
      // reject any run from this client with `engine_version_mismatch` at
      // submit time regardless (scoreSubmission, api/arcade.ts), so this
      // client-side check is what stops a stale player from investing a
      // full run — and burning further tries on repeat attempts once this
      // one predictably scores zero — into something that can never score.
      // See D1 in the whole-branch review.
      if (result.engineVersion !== ENGINE_VERSION) {
        setTriesMessage('This app just updated — reload to keep playing ranked runs.');
        return;
      }
      setRunId(result.runId);
      setSeed(result.seed);
      setRunToken((t) => t + 1);
      rankedSubmittedRef.current = false;
      setActiveLane('ranked');
      setView('playing-ranked');
    } catch (err) {
      // `reason` travels inside the parsed JSON error body (see
      // resolveStartDecision in api/arcade.ts), not on the error object
      // itself — apiFetch only ever throws a bare ApiError(status, message).
      const body = err instanceof ApiError ? err.body : undefined;
      const reason = body && typeof body === 'object' ? (body as { reason?: string }).reason : undefined;
      setTriesMessage((reason && TRIES_MESSAGES[reason]) || "Couldn't start a run — try again.");
    } finally {
      setStartBusy(false);
    }
  }

  // Practice deliberately never touches `startRun` (or any other network
  // call) — the seed is minted locally, right here, and that's the whole
  // point: no backend call means no backend to abuse.
  function handlePlayPractice() {
    setRunId(null);
    setSeed(Math.floor(Math.random() * 0xffffffff));
    setRunToken((t) => t + 1);
    setTriesMessage(null);
    setStatusMessage(null);
    setActiveLane('practice');
    setView('playing-practice');
  }

  // Shared by the two ways a ranked run's result can reach the server:
  // natural death (handleGameOver) and the player closing out mid-run
  // (handleClose). Guarded so whichever fires first wins and the other is a
  // no-op — a run must be submitted exactly once. A partial log submitted
  // from a close is a legitimate run: the server's `replay` keeps stepping
  // an unsteered snake from the last recorded input until it dies on its
  // own, so it scores honestly, and since the board keeps best-per-address,
  // submitting early can only help the player or do nothing — never hurt.
  function submitRankedRun(inputs: InputEvent[], score: number) {
    if (rankedSubmittedRef.current) return;
    rankedSubmittedRef.current = true;
    const address = userAddress;
    const currentRunId = runId;
    setRunId(null);
    if (!address || !currentRunId) return;
    submitRun(address, currentRunId, inputs, score)
      .then((submitResult) => {
        setStatusMessage(
          'queued' in submitResult && submitResult.queued
            ? "Saved — we'll post your score when you're back online."
            : null,
        );
        loadBoard(scope, scope === 'shop' ? (shopId ?? undefined) : undefined);
      })
      .catch(() => setStatusMessage("Couldn't save your score — try again."));
  }

  // A full board is a WIN, not a loss — the engine reports it as `dead: true`
  // with the score intact (SnakeCanvas has no notion of "you won"; it only
  // ever reports score + inputs). Nothing here branches on how the run
  // ended, so a full-board finish is scored and submitted exactly like any
  // other run — never presented as a failure.
  function handleGameOver(result: { score: number; inputs: InputEvent[] }) {
    if (activeLane === 'ranked') {
      setView('board');
      setActiveLane(null);
      submitRankedRun(result.inputs, result.score);
    } else if (activeLane === 'practice') {
      if (userAddress) {
        writePersonalBest(userAddress, result.score);
        setPersonalBest(readPersonalBest(userAddress));
      }
      setView('board');
      setActiveLane(null);
    }
  }

  // The in-game close (X) sits at the top of the exact area a player is
  // actively swiping in — a stray tap must not silently forfeit a
  // already-spent ranked try. If a ranked run is still live, pull whatever
  // it's recorded so far via the imperative handle and submit it through
  // the same path (and therefore the same offline queue on failure) as a
  // natural game over, rather than just discarding it. Practice has no try
  // to lose and nothing to submit, so closing it is a plain close.
  function handleClose() {
    if (activeLane === 'ranked' && !rankedSubmittedRef.current) {
      const runState = canvasHandleRef.current?.getRunState();
      if (runState) submitRankedRun(runState.inputs, runState.score);
    }
    setActiveLane(null);
    setView('board');
    onClose();
  }

  function handleReport(address: string) {
    setReportStatus('idle');
    reportPlayer(address)
      .then(() => setReportStatus('sent'))
      .catch(() => setReportStatus('error'));
  }

  if (view === 'playing-ranked' || view === 'playing-practice') {
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col">
        <div className="max-w-md mx-auto w-full flex flex-col h-full">
          <div className="flex-none flex items-center justify-between px-[22px] pt-[calc(env(safe-area-inset-top)+16px)] pb-3">
            <p className="font-heading text-[9px] text-body-grey uppercase tracking-[1px]">
              {view === 'playing-ranked' ? 'Ranked run' : 'Practice'}
            </p>
            <button type="button" onClick={handleClose} aria-label="Close" className="text-body-grey">
              <X size={18} />
            </button>
          </div>
          {/* No `items-center justify-center` here on purpose — that used to
              cap SnakeCanvas at its own intrinsic size and left a dead band
              of screen below the D-pad (Nik's on-device feedback). Stretching
              this flex child instead hands SnakeCanvas the full remaining
              height so its own `justify-between` can put the board at the
              top and the D-pad low, with the board itself rendering wider
              too (see SnakeCanvas's own max-width). `min-h-0` keeps this
              flex child from refusing to shrink below its content's natural
              height inside the column above. */}
          <div className="flex-1 flex flex-col px-[22px] pb-6 min-h-0">
            <SnakeCanvas key={runToken} ref={canvasHandleRef} seed={seed} onGameOver={handleGameOver} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col overflow-hidden">
      <div className="max-w-md mx-auto w-full flex flex-col h-full">
        <div className="flex-none relative overflow-hidden bg-coral rounded-b-[24px] px-[22px] pb-[22px] pt-[calc(env(safe-area-inset-top)+16px)]">
          <div className="absolute -top-[34px] -right-[26px] w-[126px] h-[126px] pixel-circle bg-accent-alt" />
          <button
            onClick={handleClose}
            aria-label="Close"
            className="absolute right-3 p-2 text-white/90 hover:text-white"
            style={{ top: 'calc(env(safe-area-inset-top) + 8px)' }}
          >
            <X size={18} />
          </button>
          <p className="relative font-heading text-[8px] text-white/85 tracking-[1px] uppercase mb-2.5">
            Bean Snake
          </p>
          <p className="relative font-grotesk font-bold text-[26px] leading-[1.1] text-white pr-8">
            This week's board
          </p>
          <div className="relative flex gap-2 mt-[18px]">
            <button
              type="button"
              onClick={() => handleScopeChange('global')}
              aria-pressed={scope === 'global'}
              className={scope === 'global' ? CHIP_ACTIVE : CHIP_INACTIVE}
            >
              All shops
            </button>
            <button
              type="button"
              onClick={() => handleScopeChange('shop')}
              aria-pressed={scope === 'shop'}
              disabled={!shopId}
              className={`${scope === 'shop' ? CHIP_ACTIVE : CHIP_INACTIVE} disabled:opacity-40`}
            >
              Your shop
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-[22px] pt-5 pb-6 flex flex-col gap-3.5">
          {triesMessage && (
            <p className="font-grotesk font-medium text-[13px] text-error text-center">{triesMessage}</p>
          )}
          {statusMessage && (
            <p className="font-grotesk font-medium text-[13px] text-body-grey text-center">{statusMessage}</p>
          )}
          {reportStatus === 'sent' && (
            <p className="font-grotesk font-medium text-[13px] text-success text-center">
              Report sent — thanks for keeping the board fair.
            </p>
          )}
          {reportStatus === 'error' && (
            <p className="font-grotesk font-medium text-[13px] text-error text-center">
              Couldn't send your report — try again.
            </p>
          )}

          {boardError ? (
            <div className="bg-white pixel-corners p-[18px] text-center">
              <p className="font-grotesk font-bold text-[15px] text-primary mb-3">{boardError}</p>
              <button
                type="button"
                onClick={() => loadBoard(scope, scope === 'shop' ? (shopId ?? undefined) : undefined)}
                className="pixel-corners-sm bg-coral text-white font-grotesk font-bold text-[13px] py-2.5 px-5"
              >
                Retry
              </button>
            </div>
          ) : boardLoading && !board ? (
            <p className="font-heading text-[9px] text-body-grey text-center py-6 animate-pulse">Loading…</p>
          ) : (
            <Leaderboard rows={board?.rows ?? []} viewer={board?.viewer ?? null} onReport={handleReport} />
          )}

          {userAddress && (
            <p className="font-grotesk font-medium text-[12px] text-body-grey text-center">
              Your practice best: {personalBest}
            </p>
          )}

          <div className="flex flex-col gap-2.5 mt-1">
            <button
              type="button"
              onClick={handlePlayRanked}
              disabled={startBusy}
              className="pixel-corners-sm bg-coral text-white font-grotesk font-bold text-[15px] py-[13px] disabled:opacity-50"
            >
              {startBusy ? '…' : 'Play ranked'}
            </button>
            <button
              type="button"
              onClick={handlePlayPractice}
              className="pixel-corners-sm bg-white border-2 border-hairline text-primary font-grotesk font-bold text-[15px] py-[13px]"
            >
              Practice · Free
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
